/**
 * Shared Portfolio feature (PE-1831 follow-up) — ONE admin page across every solution that
 * ships a portfolio-service-backed gallery: charter (tags booking-service SERVICES), atelier
 * and rack-and-box (both tag product-service VARIANTS). All three sit on the same
 * portfolio-service composition and return the same additive wire shape (`lineItems`/`total`),
 * so the wire types are re-exported from charter's existing feature (the first of the three
 * to ship) instead of duplicated — see osprey/src/features/charter/types.ts.
 */
import type {
  CharterMedia,
  PortfolioLineItem,
  PortfolioWork as CharterPortfolioWork,
} from '../charter/types';

export type { CharterMedia, PortfolioLineItem };

/**
 * charter's `PortfolioWork` already carries `serviceIds` (its own tag ids) plus the shared
 * `lineItems`/`total` snapshot. atelier and rack-and-box additionally return `variantIds`
 * (their tag ids) on the wire — additive, so it's added here rather than duplicating the
 * whole interface.
 */
export type PortfolioWork = CharterPortfolioWork & { variantIds?: string[] };

/** The three solutions that ship a portfolio admin page today (their watchman Solution slugs). */
export const PORTFOLIO_SOLUTIONS = ['charter', 'atelier', 'rack-and-box'] as const;
export type PortfolioSolutionId = (typeof PORTFOLIO_SOLUTIONS)[number];

export const isPortfolioSolution = (
  solutionId: string | null | undefined,
): solutionId is PortfolioSolutionId =>
  (PORTFOLIO_SOLUTIONS as readonly string[]).includes(solutionId ?? '');

interface PortfolioWorkBase {
  title: string;
  caption?: string;
  mediaIds: string[];
}

/**
 * charter tags booking-service SERVICES (optional, matching charter-api's own DTO); atelier
 * and rack-and-box tag product-service VARIANTS (required array — empty is fine — matching
 * both of their `CreatePortfolioWorkDto`s, which have no `@IsOptional()` on `variantIds`).
 */
export type CreatePortfolioWorkPayload =
  | (PortfolioWorkBase & { serviceIds?: string[] })
  | (PortfolioWorkBase & { variantIds: string[] });

/** Distributes `Partial<T>` across a union instead of collapsing it to the common-keys-only shape. */
type DistributivePartial<T> = T extends unknown ? Partial<T> : never;

export type UpdatePortfolioWorkPayload = DistributivePartial<CreatePortfolioWorkPayload>;
